import { useState, useEffect, useRef } from 'react';

interface TrendingTopicsProps {
  onTopicSearch: (topic: string) => void;
  onCreateFromTopic: (topic: string) => void;
}

interface TrendingTopic {
  id: string;
  topic: string;
  category: string;
  searches: string;
  trendPct: number;
  isHot: boolean;
  icon: string;
  color: string;
  sparkline: number[];
}

const TRENDING_TOPICS: TrendingTopic[] = [
  { id: 't1', topic: 'AI Tools & Automation', category: 'Technology', searches: '18.4K', trendPct: 234, isHot: true, icon: 'ri-robot-line', color: '#06b6d4', sparkline: [30, 35, 42, 50, 65, 82, 100] },
  { id: 't2', topic: 'Passive Income Blueprint', category: 'Finance', searches: '14.2K', trendPct: 187, isHot: true, icon: 'ri-money-dollar-circle-line', color: '#f59e0b', sparkline: [25, 30, 38, 52, 68, 80, 100] },
  { id: 't3', topic: 'ChatGPT Mastery', category: 'Technology', searches: '12.9K', trendPct: 156, isHot: true, icon: 'ri-sparkling-2-line', color: '#10b981', sparkline: [20, 28, 40, 55, 70, 88, 100] },
  { id: 't4', topic: 'Hormone Health Guide', category: 'Health', searches: '11.6K', trendPct: 143, isHot: false, icon: 'ri-heart-pulse-line', color: '#ec4899', sparkline: [40, 45, 52, 62, 74, 88, 100] },
  { id: 't5', topic: 'Digital Marketing 2025', category: 'Marketing', searches: '10.8K', trendPct: 128, isHot: false, icon: 'ri-megaphone-line', color: '#f97316', sparkline: [35, 42, 50, 60, 72, 86, 100] },
  { id: 't6', topic: 'Minimalist Productivity', category: 'Self-Help', searches: '9.7K', trendPct: 119, isHot: false, icon: 'ri-focus-3-line', color: '#8b5cf6', sparkline: [45, 50, 58, 66, 76, 88, 100] },
  { id: 't7', topic: 'Python for Beginners', category: 'Technology', searches: '9.1K', trendPct: 108, isHot: false, icon: 'ri-code-s-slash-line', color: '#3b82f6', sparkline: [50, 55, 62, 70, 80, 90, 100] },
  { id: 't8', topic: 'Romantic Fiction Writing', category: 'Fiction', searches: '8.3K', trendPct: 97, isHot: false, icon: 'ri-quill-pen-line', color: '#f43f5e', sparkline: [42, 50, 58, 68, 78, 88, 100] },
  { id: 't9', topic: 'Atomic Habits System', category: 'Self-Help', searches: '7.9K', trendPct: 89, isHot: false, icon: 'ri-seedling-line', color: '#10b981', sparkline: [48, 55, 62, 70, 80, 90, 100] },
  { id: 't10', topic: 'Real Estate Investing', category: 'Finance', searches: '7.4K', trendPct: 82, isHot: false, icon: 'ri-building-line', color: '#d97706', sparkline: [52, 58, 65, 73, 82, 91, 100] },
  { id: 't11', topic: 'Plant-Based Nutrition', category: 'Health', searches: '6.8K', trendPct: 76, isHot: false, icon: 'ri-leaf-line', color: '#22c55e', sparkline: [55, 60, 67, 74, 83, 91, 100] },
  { id: 't12', topic: 'Online Business Launch', category: 'Business', searches: '6.3K', trendPct: 71, isHot: false, icon: 'ri-store-line', color: '#6366f1', sparkline: [58, 63, 70, 77, 84, 92, 100] },
];

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const H = 24;
  const W = 48;
  const step = W / (data.length - 1);

  const points = data.map((v, i) => ({
    x: i * step,
    y: H - ((v - min) / range) * H,
  }));

  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const fill = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
    + ` L ${(data.length - 1) * step} ${H} L 0 ${H} Z`;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-shrink-0">
      <path d={fill} fill={color} fillOpacity="0.15" />
      <path d={d} stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="2.5" fill={color} />
    </svg>
  );
}

function TopicCard({ topic, onClick, onCreate, rank }: { topic: TrendingTopic; onClick: () => void; onCreate: () => void; rank: number }) {
  const [hovered, setHovered] = useState(false);

  const heatColor =
    topic.trendPct >= 200 ? '#ef4444' :
    topic.trendPct >= 150 ? '#f97316' :
    topic.trendPct >= 100 ? '#f59e0b' : '#10b981';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group flex-shrink-0 w-64 bg-white border-2 rounded-2xl p-4 text-left transition-all duration-300"
      style={{
        borderColor: hovered ? topic.color : '#e2e8f0',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered ? `0 12px 32px ${topic.color}25` : 'none',
      }}
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white transition-transform duration-300"
            style={{ backgroundColor: topic.color, transform: hovered ? 'rotate(8deg) scale(1.1)' : 'none' }}
          >
            <i className={`${topic.icon} text-base`}></i>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-black text-slate-400">#{rank}</span>
              {topic.isHot && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 border border-red-200 rounded-full text-xs font-bold text-red-600">
                  <i className="ri-fire-fill text-xs"></i> HOT
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{topic.category}</p>
          </div>
        </div>

        {/* Trend badge */}
        <div className="flex flex-col items-end gap-1">
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-black"
            style={{ backgroundColor: heatColor + '15', color: heatColor }}
          >
            <i className="ri-arrow-up-line text-xs"></i>
            +{topic.trendPct}%
          </div>
          <span className="text-xs text-slate-400 font-semibold">{topic.searches}</span>
        </div>
      </div>

      {/* Topic name */}
      <p
        className="text-sm font-extrabold leading-snug mb-3 transition-colors"
        style={{ color: hovered ? topic.color : '#0f172a' }}
      >
        {topic.topic}
      </p>

      {/* Sparkline row */}
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-xs text-slate-400 mb-1">7-day trend</p>
          <MiniSparkline data={topic.sparkline} color={topic.color} />
        </div>
        <span className="text-xs text-slate-400">{topic.searches} searches</span>
      </div>

      {/* ── Two Action Buttons ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-1.5">
        <button
          onClick={onClick}
          className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap"
          style={{
            borderColor: topic.color + '50',
            backgroundColor: hovered ? topic.color + '12' : '#f8fafc',
            color: hovered ? topic.color : '#475569',
          }}
        >
          <i className="ri-search-line text-xs"></i>
          Find Templates
        </button>
        <button
          onClick={onCreate}
          className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold text-white transition-all cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: topic.color, opacity: 0.85 + (hovered ? 0.15 : 0) }}
        >
          <i className="ri-magic-line text-xs"></i>
          Create with AI
        </button>
      </div>
    </div>
  );
}

export default function TrendingTopics({ onTopicSearch, onCreateFromTopic }: TrendingTopicsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [liveCount, setLiveCount] = useState(2847);

  // Simulate live counter
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveCount(c => c + Math.floor(Math.random() * 3));
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 20);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 20);
  };

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 560 : -560, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', checkScroll); checkScroll(); }
    return () => { el?.removeEventListener('scroll', checkScroll); };
  }, []);

  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-10">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="flex items-center gap-1.5 px-3 py-1 bg-red-50 border border-red-200 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-xs font-extrabold text-red-600 uppercase tracking-wider">Live</span>
              </div>
              <h2 className="text-xl font-extrabold text-slate-900">Trending eBook Topics</h2>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 px-2.5 py-1 bg-slate-100 rounded-full">
                <i className="ri-refresh-line text-slate-400"></i>
                Updated every 10 min
              </span>
            </div>
            <p className="text-sm text-slate-500">What creators are searching and publishing this week — click any topic to find matching templates</p>
          </div>

          {/* Live counter */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 border border-orange-200 rounded-2xl flex-shrink-0">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-user-line text-orange-500 text-lg"></i>
            </div>
            <div>
              <p className="text-base font-extrabold text-orange-700 leading-none tabular-nums">{liveCount.toLocaleString()}</p>
              <p className="text-xs text-orange-500">searching now</p>
            </div>
          </div>
        </div>

        {/* ── Scrollable Topic Feed ────────────────────────────────────────── */}
        <div className="relative">
          {/* Left fade + arrow */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 flex items-center pointer-events-none">
              <button
                className="ml-1 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-all pointer-events-auto shadow-sm"
                onClick={() => scroll('left')}
              >
                <i className="ri-arrow-left-s-line text-slate-600 text-base"></i>
              </button>
            </div>
          )}
          {/* Right fade + arrow */}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end pointer-events-none">
              <button
                className="mr-1 w-9 h-9 bg-white border border-slate-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-all pointer-events-auto shadow-sm"
                onClick={() => scroll('right')}
              >
                <i className="ri-arrow-right-s-line text-slate-600 text-base"></i>
              </button>
            </div>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {TRENDING_TOPICS.map((topic, i) => (
              <TopicCard
                key={topic.id}
                topic={topic}
                rank={i + 1}
                onClick={() => onTopicSearch(topic.topic)}
                onCreate={() => onCreateFromTopic(topic.topic)}
              />
            ))}
          </div>
        </div>

        {/* ── Bottom hint ─────────────────────────────────────────────────── */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            {['🔥 Hottest', '📈 Fastest growing', '💡 Hidden gems'].map(tag => (
              <span key={tag} className="text-xs text-slate-400 font-medium">{tag}</span>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <i className="ri-arrow-left-right-line text-slate-300"></i>
            Scroll to see all {TRENDING_TOPICS.length} trending topics
          </div>
        </div>
      </div>
    </div>
  );
}
